// One shift as an editable document for the new screens: loads the shift and the station setup,
// keeps the entry being typed, computes every total live (computeShift, the same calculation the
// server uses), autosaves, and submits. The data and payload are exactly those of the current
// shift screen (pages/ShiftEntry.jsx), so a shift saved here and there is the same shift.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { computeShift, pricesAt, shiftStartISO } from "@/lib/format";

export const LOCKED_EDIT = (status) => status !== "draft";
export function num(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}
const hasValue = (v) => v !== "" && v !== null && v !== undefined;
export const uid = () => Math.random().toString(36).slice(2, 10);

function buildEntry(s, c, prices) {
  const activePumps = c.pumps.filter((p) => p.active !== false);
  const activeTanks = c.tanks.filter((t) => t.active !== false);
  const activeItems = c.nonfuel_items.filter((i) => i.active !== false);
  const activeMethods = c.payment_methods.filter((m) => m.active !== false);
  const fuelMap = Object.fromEntries((s.fuel_readings || []).map((r) => [String(r.pump_id), r]));
  const tankMap = Object.fromEntries((s.tank_dips || []).map((r) => [String(r.tank_id), r]));
  const itemMap = Object.fromEntries((s.nonfuel || []).map((r) => [String(r.item_id), r]));
  const payMap = Object.fromEntries((s.payments || []).map((r) => [`${r.attendant_id || ""}|${r.method_id}`, r]));
  const payGroups = [...c.attendants.filter((a) => a.active !== false).map((a) => a.id), ""];
  const tillMap = Object.fromEntries((s.till_ledger || []).map((r) => [String(r.till_id), r]));
  const locked = LOCKED_EDIT(s.status);
  return {
    supervisor_name: s.supervisor_name || "",
    fuel_prices: prices.price,
    fuel_cost_prices: prices.cost,
    pump_attendants: { ...(s.pump_attendants || {}) },
    attendant_expected: { ...(s.attendant_expected || {}) },
    attendant_lube_sales: { ...(s.attendant_lube_sales || {}) },
    till_attendants: { ...(s.till_attendants || {}) },
    nonfuel_group_attendants: { ...(s.nonfuel_group_attendants || {}) },
    nonfuel_payments: (s.nonfuel_payments || []).filter((p) => num(p.amount) || p.attendant_id).map((p) => ({
      _k: p._k || uid(), attendant_id: p.attendant_id || s.nonfuel_group_attendants?.[p.group] || "",
      group: p.group || "lube", method_id: p.method_id || "", amount: p.amount ?? "", reference: p.reference || "", till_id: p.till_id || "",
    })),
    till_ledger: (c.tills || []).filter((t) => t.active !== false).map((t) => ({
      till_id: t.id, opening_balance: tillMap[t.id]?.opening_balance ?? "", withdrawal: tillMap[t.id]?.withdrawal ?? "",
      actual_balance: tillMap[t.id]?.actual_balance ?? "", actual_balance_at: tillMap[t.id]?.actual_balance_at ?? "",
      _openingLocked: hasValue(tillMap[t.id]?.opening_balance),
    })),
    fuel_readings: activePumps.map((p) => ({
      pump_id: p.id, product_id: p.product_id,
      opening: fuelMap[p.id]?.opening ?? "", closing: fuelMap[p.id]?.closing ?? "", testing: fuelMap[p.id]?.testing ?? "",
      elec_opening_l: fuelMap[p.id]?.elec_opening_l ?? "", elec_closing_l: fuelMap[p.id]?.elec_closing_l ?? "",
      elec_opening_kes: fuelMap[p.id]?.elec_opening_kes ?? "", elec_closing_kes: fuelMap[p.id]?.elec_closing_kes ?? "",
      _openingLocked: hasValue(fuelMap[p.id]?.opening), _elecOpeningLocked: hasValue(fuelMap[p.id]?.elec_opening_l),
    })),
    tank_dips: activeTanks.map((t) => ({
      tank_id: t.id, product_id: t.product_id,
      opening_dip: tankMap[t.id]?.opening_dip ?? "", deliveries: tankMap[t.id]?.deliveries ?? "", rtt: tankMap[t.id]?.rtt ?? "",
      closing_dip: tankMap[t.id]?.closing_dip ?? "", tls_opening_dip: tankMap[t.id]?.tls_opening_dip ?? "", tls_closing_dip: tankMap[t.id]?.tls_closing_dip ?? "",
      _openingLocked: hasValue(tankMap[t.id]?.opening_dip), _tlsOpeningLocked: hasValue(tankMap[t.id]?.tls_opening_dip),
    })),
    nonfuel: activeItems.map((it) => ({
      item_id: it.id,
      unit_price: locked ? (itemMap[it.id]?.unit_price ?? it.unit_price) : it.unit_price,
      cost_price: locked ? (itemMap[it.id]?.cost_price ?? it.cost_price) : it.cost_price,
      opening: itemMap[it.id]?.opening ?? "", received: itemMap[it.id]?.received ?? "", returned: itemMap[it.id]?.returned ?? "",
      closing: itemMap[it.id]?.closing ?? "", shop_opening: itemMap[it.id]?.shop_opening ?? "", shop_closing: itemMap[it.id]?.shop_closing ?? "",
      _openingLocked: hasValue(itemMap[it.id]?.opening), _shopOpeningLocked: hasValue(itemMap[it.id]?.shop_opening),
    })),
    payments: payGroups.flatMap((attId) => activeMethods.map((m) => {
      const p = payMap[`${attId}|${m.id}`];
      return { attendant_id: attId, method_id: m.id, amount: p?.amount ?? "", reference: p?.reference ?? "" };
    })),
    expenses: (s.expenses || []).map((e) => ({ ...e, _k: e._k || uid() })),
    till_transfers: (s.till_transfers || []).map((t) => ({ ...t, _k: t._k || uid() })),
    account_receipts: (s.account_receipts || []).map((a) => ({ ...a, _k: a._k || uid() })),
    invoice_lines: (s.invoice_lines || []).map((l) => ({ ...l, _k: l._k || uid() })),
    payment_transactions: (s.payment_transactions || []).map((t) => ({ ...t, _k: t._k || uid() })),
    tank_deliveries: (s.tank_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
    nonfuel_deliveries: (s.nonfuel_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
    notes: s.notes || "",
  };
}

function forCompute(entry, shift) {
  return {
    fuel_prices: entry.fuel_prices, fuel_cost_prices: entry.fuel_cost_prices,
    fuel_readings: entry.fuel_readings.map((r) => ({ ...r, opening: num(r.opening), closing: num(r.closing), testing: num(r.testing),
      elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l), elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes) })),
    tank_dips: entry.tank_dips.map((r) => ({ ...r, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries), rtt: num(r.rtt), closing_dip: num(r.closing_dip),
      tls_opening_dip: num(r.tls_opening_dip), tls_closing_dip: num(r.tls_closing_dip) })),
    tank_deliveries: entry.tank_deliveries.map((d) => ({ ...d, invoice_amount: num(d.invoice_amount), invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity) })),
    nonfuel: entry.nonfuel.map((r) => ({ ...r, opening: num(r.opening), received: num(r.received), returned: num(r.returned), closing: num(r.closing),
      shop_opening: num(r.shop_opening), shop_closing: num(r.shop_closing), unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
    nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({ ...d, invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity) })),
    payments: entry.payments.map((r) => ({ ...r, amount: num(r.amount) })),
    payment_transactions: entry.payment_transactions.map((t) => ({ ...t, amount: num(t.amount) })),
    expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
    till_transfers: entry.till_transfers.map((t) => ({ ...t, amount: num(t.amount) })),
    account_receipts: entry.account_receipts.map((a) => ({ ...a, amount: num(a.amount) })),
    invoice_lines: entry.invoice_lines.map((l) => ({ ...l, amount: num(l.amount), litres: num(l.litres) })),
    pump_attendants: entry.pump_attendants, attendant_expected: entry.attendant_expected, attendant_lube_sales: entry.attendant_lube_sales,
    till_attendants: entry.till_attendants,
    till_ledger: entry.till_ledger.map((t) => ({ ...t, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal),
      actual_balance: t.actual_balance === "" ? null : num(t.actual_balance) })),
    nonfuel_group_attendants: entry.nonfuel_group_attendants,
    nonfuel_payments: entry.nonfuel_payments.map((p) => ({ ...p, amount: num(p.amount), till_id: p.till_id || null })),
    date: shift?.date, mpesa_api_by_till: shift?.mpesa_api_by_till || {}, vivo_includes_nonfuel: !!shift?.vivo_includes_nonfuel,
    mpesa_api_live: !!shift?.mpesa_api_live, mpesa_code_lookup: shift?.mpesa_code_lookup || {},
  };
}

function buildPayload(entry, cfg) {
  const kindOf = (mid) => (cfg?.payment_methods || []).find((m) => m.id === mid)?.kind;
  return {
    supervisor_name: entry.supervisor_name,
    fuel_prices: Object.fromEntries(Object.entries(entry.fuel_prices).map(([k, v]) => [k, num(v)])),
    fuel_cost_prices: Object.fromEntries(Object.entries(entry.fuel_cost_prices || {}).map(([k, v]) => [k, num(v)])),
    pump_attendants: entry.pump_attendants || {},
    attendant_expected: Object.fromEntries(Object.entries(entry.attendant_expected || {}).map(([k, v]) => [k, v === "" ? "" : num(v)])),
    attendant_lube_sales: Object.fromEntries(Object.entries(entry.attendant_lube_sales || {}).map(([k, v]) => [k, num(v)])),
    till_attendants: entry.till_attendants || {},
    till_ledger: entry.till_ledger.map((t) => ({ till_id: t.till_id, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal),
      actual_balance: t.actual_balance === "" ? null : num(t.actual_balance), actual_balance_at: t.actual_balance_at || null })),
    nonfuel_group_attendants: entry.nonfuel_group_attendants || {},
    nonfuel_payments: entry.nonfuel_payments.map((p) => ({ _k: p._k, attendant_id: p.attendant_id || null, group: p.group, method_id: p.method_id,
      amount: num(p.amount), reference: p.reference || "", till_id: p.till_id || null })),
    fuel_readings: entry.fuel_readings.map((r) => ({ pump_id: r.pump_id, product_id: r.product_id, opening: num(r.opening), closing: num(r.closing),
      testing: num(r.testing), elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l),
      elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes) })),
    tank_dips: entry.tank_dips.map((r) => ({ tank_id: r.tank_id, product_id: r.product_id, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries),
      rtt: num(r.rtt), closing_dip: num(r.closing_dip), tls_opening_dip: num(r.tls_opening_dip), tls_closing_dip: num(r.tls_closing_dip) })),
    tank_deliveries: entry.tank_deliveries.map((d) => ({ _k: d._k, tank_id: d.tank_id, invoice_no: d.invoice_no || "", date: d.date || "",
      product_id: d.product_id || null, invoice_amount: num(d.invoice_amount), invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity) })),
    nonfuel: entry.nonfuel.map((r) => ({ item_id: r.item_id, opening: num(r.opening), received: num(r.received), returned: num(r.returned), closing: num(r.closing),
      ...(cfg?.station?.lube_shop_front ? { shop_opening: num(r.shop_opening), shop_closing: num(r.shop_closing) } : {}),
      unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
    nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({ _k: d._k, item_id: d.item_id, invoice_no: d.invoice_no || "", date: d.date || "",
      invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity) })),
    payments: entry.payments.map((r) => ({ attendant_id: r.attendant_id || null, method_id: r.method_id, amount: num(r.amount), reference: r.reference })),
    payment_transactions: entry.payment_transactions.map((t) => ({ attendant_id: t.attendant_id || null, method_id: t.method_id, amount: num(t.amount),
      reference: t.reference || "", note: t.note || "",
      ...(kindOf(t.method_id) === "card" ? { txn_date: t.txn_date || "", auth_code: t.auth_code || "", product: t.product || "" } : {}) })),
    expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
    till_transfers: entry.till_transfers.map((t) => ({ _k: t._k, landed_till_id: t.landed_till_id || null, meant_till_id: t.meant_till_id || null,
      amount: num(t.amount), reference: t.reference || "", note: t.note || "" })),
    account_receipts: entry.account_receipts.map((a) => ({ _k: a._k, account_id: a.account_id || null, method_id: a.method_id || null,
      till_id: a.till_id || null, amount: num(a.amount), reference: a.reference || "", note: a.note || "" })),
    invoice_lines: entry.invoice_lines.map((l) => ({ _k: l._k, attendant_id: l.attendant_id || null, method_id: l.method_id || null,
      customer_id: l.customer_id || null, invoice_no: l.invoice_no || "", vehicle_reg: l.vehicle_reg || "", product_id: l.product_id || null,
      litres: num(l.litres), amount: num(l.amount) })),
    notes: entry.notes,
  };
}

export default function useShiftDoc(id) {
  const [cfg, setCfg] = useState(null);
  const [shift, setShift] = useState(null);
  const [entry, setEntry] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    const [cfgRes, shiftRes] = await Promise.all([api.get("/config"), api.get(`/shifts/${id}`)]);
    const s = shiftRes.data, c = cfgRes.data;
    const prices = { price: { ...(s.fuel_prices || {}) }, cost: { ...(s.fuel_cost_prices || {}) } };
    if (!LOCKED_EDIT(s.status) && !s.fuel_prices_locked) {
      try {
        const pr = await api.get("/config/fuel-prices");
        const at = pricesAt(pr.data, shiftStartISO(s.date, s.shift_type, c.station?.shift_hours));
        Object.assign(prices.price, at.price);
        Object.assign(prices.cost, at.cost);
      } catch { /* keep the shift's own prices */ }
    }
    setCfg(c);
    setShift(s);
    setEntry(buildEntry(s, c, prices));
    setDirty(false);
  }, [id]);

  useEffect(() => { load().catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail))); }, [load]);

  const computed = useMemo(() => (cfg && entry ? computeShift(forCompute(entry, shift), cfg) : null), [cfg, entry, shift]);
  const readOnly = !shift || LOCKED_EDIT(shift.status);

  // change the entry: update((draft) => { draft.notes = "x" })
  const update = useCallback((fn) => {
    setEntry((prev) => { const next = structuredClone(prev); fn(next); return next; });
    setDirty(true);
  }, []);

  const entryRef = useRef(entry); entryRef.current = entry;
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const save = useCallback(async ({ silent = false } = {}) => {
    if (!entryRef.current) return;
    if (!silent) setSaving(true);
    try {
      const { data } = await api.put(`/shifts/${id}`, buildPayload(entryRef.current, cfgRef.current));
      setShift(data);
      setDirty(false);
      setSaveError(null);
      if (!silent) toast.success("Saved");
    } catch (e) {
      const msg = e.response?.status === 401
        ? "Not saved: your login has expired. Sign in again in another tab, then save. Do not close this page."
        : `Not saved: ${formatApiErrorDetail(e.response?.data?.detail)}`;
      toast.error(msg, { duration: 15000 });
      setSaveError(msg);
    } finally {
      if (!silent) setSaving(false);
    }
  }, [id]);

  // autosave a moment after typing stops, and when the tab is hidden
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const t = setTimeout(() => save({ silent: true }), 1500);
    return () => clearTimeout(t);
  }, [entry, dirty, readOnly, save]);
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden" && dirtyRef.current) save({ silent: true }); };
    const onUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("beforeunload", onUnload); };
  }, [save]);

  const submit = useCallback(async (note) => {
    try {
      if (dirtyRef.current) await api.put(`/shifts/${id}`, buildPayload(entryRef.current, cfgRef.current));
      await api.post(`/shifts/${id}/submit`, { note: note || "" });
      await load();
      toast.success("Shift submitted");
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.validation_errors ? "Can't submit yet: " + d.validation_errors.join(" ") : formatApiErrorDetail(d), { duration: 12000 });
      return false;
    }
  }, [id, load]);

  return { cfg, shift, entry, computed, update, save, submit, dirty, saving, saveError, loadError, readOnly, reload: load };
}
